// Client photos for posts: find a client, see the photos on their record,
// and only use the ones they've said yes to sharing on social media.
//
// That yes is the photo's share consent, recorded by the server with who and
// when (PUT client-photo/:id/share-consent), or the client's yes for all
// their photos (consent.social). The old `social` tag is NOT consent: it had
// no record of who or when, so it is ignored (docs/SOCIAL_BACKEND.md §2).
import { hairdresserService } from '../../../../../_services/hairdresser.service'
import { clientPhotosService, effectiveShareConsent } from '../../../../../_services/clientPhotos.service'

// The old per-photo tag. Kept so the lightbox can hide it from the tag list;
// never read as consent.
export const SOCIAL_TAG = 'social'

const nameOf = (c) => c.name
  || [c.firstName || c.first_name, c.lastName || c.last_name].filter(Boolean).join(' ')
  || 'Client'

// Clients by name, email or phone (the POS picker's call: no Redux, so the
// Clients page list is left alone)
export const searchClients = async (q = '', page = 1, limit = 20) => {
  const res = await hairdresserService.getClientsPaginated({ searchKey: q, page, limit, sortField: 'name', sortOrder: 'asc' })
  const data = (res && res.data && (res.data.responseData || res.data.data)) || {}
  const rows = data.clients || data.clientList || []
  return {
    clients: (Array.isArray(rows) ? rows : [])
      .map(c => ({ id: c.userId || c._id, name: nameOf(c), avatar: c.localAvatar || (c.userImage && c.userImage.circleImage) || null }))
      .filter(c => c.id && c.name && c.name !== 'Unknown'),
    hasMore: Array.isArray(rows) && rows.length >= limit,
  }
}

const tagsOf = (p) => (Array.isArray(p.tags) ? p.tags.map(t => String(t).toLowerCase()) : [])

// 'social' (yes), 'noFace' (yes, face not shown) or 'none'
export const shareConsentOf = (photo, consent) => effectiveShareConsent(photo, consent)
export const canShare = (photo, consent) => shareConsentOf(photo, consent) !== 'none'
export const photoTag = (photo) => {
  const tags = tagsOf(photo)
  if (tags.includes('before')) return 'before'
  if (tags.includes('after')) return 'after'
  return null
}

const toPhoto = (p) => ({
  id: p.id || p._id,
  url: p.url,
  thumb: p.thumbUrl || p.url,
  tags: tagsOf(p),
  capturedAt: p.capturedAt,
  service: p.bookingService || '',
  shareConsent: p.shareConsent,
  shareConsentAt: p.shareConsentAt || null,
  shareConsentEffective: p.shareConsentEffective,
  clientConsent: p.clientConsent,
  client: p.client ? { id: p.client.userId || p.client.salonClientId, name: p.client.name || 'Client' } : null,
})

// A client's photos, newest first
export const loadClientPhotos = async (clientId) => {
  const res = await clientPhotosService.fetchClientPhotos(clientId, { limit: 60 })
  const photos = (res.photos || [])
    .map(toPhoto)
    .filter(p => p.url)
    .sort((a, b) => new Date(b.capturedAt || 0) - new Date(a.capturedAt || 0))
  return { photos, consent: res.consent || {} }
}

// Recent photos across every client the salon (or this stylist) can see,
// newest first; before/after slots ask for that tag
export const loadSalonPhotos = async ({ slot = null, cursor = null, limit = 30 } = {}) => {
  const tag = slot === 'before' || slot === 'after' ? slot : null
  const res = await clientPhotosService.searchSalonPhotos({ tag, cursor, limit })
  return { photos: (res.photos || []).map(toPhoto).filter(p => p.url), nextCursor: res.nextCursor }
}

// The salon confirms the client said yes (the same declaration as the photo
// storage consent: "I have permission"). The server records who and when.
export const markShareable = async (clientId, photo) => {
  const { photo: saved } = await clientPhotosService.setShareConsent(photo.id, 'social')
  return {
    ...photo,
    shareConsent: 'social',
    shareConsentAt: (saved && saved.shareConsentAt) || new Date().toISOString(),
    shareConsentEffective: 'social',
  }
}
